"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import clsx from "clsx";
import React from "react";

type NavLinkProps = {
  href: string;
  className?: string;
  activeClassName?: string;
  role?: string;
  exact?: boolean;
  children: React.ReactNode;
};

/**
 * Determines if the current path matches the link href.
 * @param currentPath - The current pathname from usePathname()
 * @param href - The link's href to match against
 * @param exact - If true, only matches if paths are exactly equal (useful for dashboard/home links)
 * @returns Whether the link should be considered active
 */
export function isLinkActive(currentPath: string, href: string, exact: boolean = false): boolean {
  // Strip query strings and hash fragments from both paths
  const pathOnly = currentPath.split('?')[0].split('#')[0];
  const hrefPathOnly = href.split('?')[0].split('#')[0];
  
  // Normalize paths - remove trailing slashes for comparison
  const normalizedCurrent = pathOnly.replace(/\/$/, '');
  const normalizedHref = hrefPathOnly.replace(/\/$/, '');
  
  // Exact match after normalization
  if (normalizedCurrent === normalizedHref) {
    return true;
  }
  
  // If exact matching is required, don't match child paths
  if (exact) {
    return false;
  }
  
  // Check if current path starts with normalized href followed by "/"
  // This handles both "/conversations" and "/conversations/" matching "/conversations/monitor"
  return normalizedCurrent.startsWith(normalizedHref + '/');
}

export default function NavLink({ href, className, activeClassName, role, exact, children }: NavLinkProps) {
  const pathname = usePathname() || "";
  const isActive = isLinkActive(pathname, href, exact);
  const classes = clsx(className, isActive && activeClassName);

  return (
    <Link href={href} role={role} className={classes}>
      {children}
    </Link>
  );
}
